import moment from "moment";
import type Stripe from "stripe";

import { IStripeSubscriptionExtended, WorkspacePlanTier } from "common/interfaces";
import products, { RecurringPlanDefinition } from "common/stripeProducts";
import { guessUserCountry } from "common/utils/countries";

export const Customer = {
    getTaxId(customer: Stripe.Customer) {
        return customer?.tax_ids?.data?.[0]?.value ?? "";
    },
    getTaxIdType(customer: Stripe.Customer) {
        return customer?.tax_ids?.data?.[0]?.type ?? null;
    },
    getBillingAddress(customer: Stripe.Customer, paymentMethod: Stripe.PaymentMethod) {
        const address = customer?.address ?? paymentMethod?.billing_details.address ?? {
            city: "",
            country: guessUserCountry(),
            line1: "",
            line2: "",
            postal_code: "",
            state: ""
        };
        return Object.fromEntries(Object.entries(address).map(([key, value]) => [key, value === null ? "" : value])) as Stripe.Address;
    }
};

export const PaymentMethod = {
    getPaymentMethodName(paymentMethod: Stripe.PaymentMethod) {
        if (!paymentMethod || !paymentMethod.type) {
            return "unknown";
        }
        if (paymentMethod.type === "card" && paymentMethod.card.wallet) {
            return paymentMethod.card.wallet.type;
        } else {
            return paymentMethod.type;
        }
    },
    getFundingType(paymentMethod: Stripe.PaymentMethod | null | undefined) {
        return paymentMethod?.type === "card"
            ? paymentMethod.card?.funding ?? "unknown"
            : "unknown";
    },
    getPaymentType(paymentMethod: Stripe.PaymentMethod | null | undefined) {
        return paymentMethod?.card?.wallet?.type === "apple_pay"
            ? "apple_pay"
            : paymentMethod?.type ?? "unknown";
    },
    getDefaultPaymentMethod(subscription: IStripeSubscriptionExtended, paymentMethods: Stripe.PaymentMethod[]) {
        if (subscription?.default_payment_method) {
            return subscription.default_payment_method;
        }

        return [...paymentMethods].sort((a, b) => b.created - a.created)[0] ?? null;
    }
};

export const Subscription = {
    Schedule: {
        isInLastPhaseOfSchedule(subscription: IStripeSubscriptionExtended) {
            const currentPhase = Subscription.currentSchedulePhase(subscription);
            const pendingPhase = Subscription.pendingSchedulePhase(subscription);

            return !!(currentPhase && !pendingPhase);
        },
        currentPhaseIndex(subscription: IStripeSubscriptionExtended) {
            const schedule = subscription?.schedule;
            if (!schedule?.phases?.length) {
                return null;
            }

            const currentStripeDate = subscription.test_clock
                ? subscription.test_clock.frozen_time
                : Date.now() / 1000;

            const currentPhaseIndex = schedule.phases.findIndex(
                p => p.start_date <= currentStripeDate && p.end_date >= currentStripeDate
            );

            if (currentPhaseIndex === -1) {
                // no matching phase
                return null;
            }
            return currentPhaseIndex;
        }
    },

    getTrialDaysRemaining(subscription: IStripeSubscriptionExtended) {
        try {
            const isInTrial = subscription?.status === "trialing";
            return isInTrial ? Math.ceil(
                (subscription?.trial_end * 1000 - new Date().getTime()) / 1000 / 60 / 60 / 24,
            ) : 0;
        } catch {
            return 0;
        }
    },
    getProrationCredit(subscription: IStripeSubscriptionExtended, {
        newPlanAmountDollars,
        newSeats,
    }) {
        if (!subscription) {
            return 0;
        }

        const currentItem = subscription?.items?.data?.[0];
        const currentInterval = currentItem?.price?.recurring?.interval;
        const currentSeats = currentItem?.quantity ?? 0;
        const currentUnitAmountCents = currentItem?.price?.unit_amount ?? 0;
        const currentPeriodEnd = subscription?.current_period_end;
        const currentPeriodStart = subscription?.current_period_start;
        const trialDaysRemaining = Subscription.getTrialDaysRemaining(subscription);

        if (trialDaysRemaining > 0) {
            // if trial no proration
            return 0;
        } else if (!currentItem || !currentPeriodEnd || !currentPeriodStart || !currentInterval) {
            return 0;
        } else {
            //proration logic
            const currentStripeDate = subscription.test_clock
                ? moment.unix(subscription.test_clock.frozen_time)
                : moment();
            const periodEnd = moment.unix(currentPeriodEnd);
            const periodStart = moment.unix(currentPeriodStart);

            const totalMsInPeriod = periodEnd.valueOf() - periodStart.valueOf();
            const remainingMsInPeriod = periodEnd.valueOf() - currentStripeDate.valueOf();

            // Check for invalid states (e.g., expired period)
            if (totalMsInPeriod <= 0 || remainingMsInPeriod <= 0) {
                // Cannot prorate, charge full amount
                return 0;
            } else {
                // Calculate prorated credit and cost
                const fractionRemaining = remainingMsInPeriod / totalMsInPeriod;

                // Credit for unused time on current plan
                const currentTotalCostDollars = (currentUnitAmountCents * currentSeats) / 100;
                const creditForCurrentPlan = currentTotalCostDollars * fractionRemaining;
                const newTotalCostDollars = newPlanAmountDollars * newSeats;
                const costForUpgradedPlan = newTotalCostDollars * fractionRemaining;
                return newTotalCostDollars - costForUpgradedPlan + creditForCurrentPlan;
            }
        }
    },
    currentSchedulePhase(subscription: IStripeSubscriptionExtended): Stripe.SubscriptionSchedule.Phase {
        if (!subscription) {
            return null;
        }
        if (!subscription.schedule) {
            return null;
        }

        const currentPhaseIndex = Subscription.Schedule.currentPhaseIndex(subscription);

        if (currentPhaseIndex == null) {
            return null;
        }

        return subscription.schedule.phases[currentPhaseIndex];
    },
    pendingSchedulePhase(subscription: IStripeSubscriptionExtended): Stripe.SubscriptionSchedule.Phase {
        if (!subscription) {
            return null;
        }

        const currentPhaseIndex = Subscription.Schedule.currentPhaseIndex(subscription);

        if (currentPhaseIndex == null) {
            return null;
        }

        if (currentPhaseIndex === subscription.schedule.phases.length - 1) {
            // We are in the final phase of the schedule, no pending changes
            return null;
        }

        return subscription.schedule.phases[currentPhaseIndex + 1];
    },
    hasPendingCancellation(subscription: IStripeSubscriptionExtended): boolean {
        if (!subscription) {
            return false;
        }
        return subscription.cancel_at_period_end;
    },
    hasPastDuePayment(subscription: IStripeSubscriptionExtended): boolean {
        return subscription?.status === "past_due";
    },
    hasPendingDowngrade(subscription: IStripeSubscriptionExtended): boolean {
        if (!subscription) {
            return false;
        }

        const currentPhase = Subscription.currentSchedulePhase(subscription);
        if (!currentPhase) {
            return false;
        }

        const pendingPhase = Subscription.pendingSchedulePhase(subscription);
        if (!pendingPhase) {
            return false;
        }

        if (!currentPhase.items?.[0]?.price && !pendingPhase.items?.[0]?.price) {
            return false;
        }

        const currentPriceId = typeof currentPhase.items[0].price === "string"
            ? currentPhase.items[0].price
            : (currentPhase.items[0].price as Stripe.Price)?.id;
        const pendingPriceId = typeof pendingPhase.items[0].price === "string"
            ? pendingPhase.items[0].price
            : (pendingPhase.items[0].price as Stripe.Price)?.id;

        if (!currentPriceId || !pendingPriceId) {
            return false;
        }
        const currentTier = Plans.getRecurringPlanTier(currentPriceId);
        const pendingTier = Plans.getRecurringPlanTier(pendingPriceId);

        const downgradedFromTeam =
            currentTier === WorkspacePlanTier.TEAM &&
            pendingTier === WorkspacePlanTier.PRO;

        const downgradedFromEnterprise =
            currentTier === WorkspacePlanTier.ENTERPRISE &&
            [WorkspacePlanTier.TEAM, WorkspacePlanTier.PRO].includes(pendingTier);

        return downgradedFromTeam || downgradedFromEnterprise;
    },
    daysRemainingBeforeDowngrade(subscription: IStripeSubscriptionExtended): number | null {
        if (!Subscription.hasPendingDowngrade(subscription)) {
            return null;
        }

        const schedule = subscription.schedule;
        const currentPhaseIndex = Subscription.Schedule.currentPhaseIndex(subscription);
        if (currentPhaseIndex == null) {
            return null;
        }

        const currentPhase = schedule.phases[currentPhaseIndex];
        if (!currentPhase?.end_date) {
            return null;
        }

        const currentStripeDate = subscription.test_clock
            ? subscription.test_clock.frozen_time
            : Date.now() / 1000;

        return Math.ceil(
            (currentPhase.end_date - currentStripeDate) / (60 * 60 * 24)
        );
    },
    canChangeQuantity(subscription: IStripeSubscriptionExtended): boolean {
        // This checks the state of the subscription to see if it is in a state that will support quantity changes
        // DOES NOT check permissions or other requirements
        if (!subscription) {
            return false;
        }
        if (Subscription.hasPendingDowngrade(subscription)) {
            return false;
        }
        return true;
    },
};

export const Plans = {
    getRecurringPlanData(planId: string) {
        for (const [tier, definition] of Object.entries(products)) {
            if (definition?.plans) {
                const foundPlan = definition.plans.find(plan =>
                    plan.type === "recurring" &&
                    plan.planId === planId);
                if (foundPlan) {
                    return {
                        plan: foundPlan as RecurringPlanDefinition,
                        tier: tier as WorkspacePlanTier,
                        productId: definition.productId,
                        productName: definition.productName,
                    };
                }
            }
        }
        // Nothing found
        return null;
    },
    getRecurringPlanTier(planId: string) {
        const planDefinition = Plans.getRecurringPlanData(planId);
        return planDefinition?.tier ?? null;
    },
    getRecurringPlanInterval(planId: string) {
        const planDefinition = Plans.getRecurringPlanData(planId);
        return planDefinition?.plan?.interval ?? null;
    },
    getDefaultRecurringPlanId(tier: WorkspacePlanTier, interval: "month" | "year") {
        const productDefinition = products[tier];
        if (!productDefinition) {
            return null;
        }
        const foundPlan: RecurringPlanDefinition = productDefinition.plans.find((plan): plan is RecurringPlanDefinition =>
            plan.type === "recurring" &&
            plan.interval === interval &&
            plan.isDefault &&
            !plan.isDeprecated
        );
        return foundPlan?.planId ?? null;
    }
};

type CouponSource = {
    promotion?: Stripe.PromotionCode["promotion"];
    source?: Stripe.Discount["source"];
};

export function getCoupon(entity?: CouponSource): Stripe.Coupon | null {
    if (!entity) {
        return null;
    }

    const source = entity.promotion ?? entity.source;
    const coupon = source?.coupon;
    if (!coupon || typeof coupon === "string") {
        return null;
    }
    return coupon;
}

export const StripeError = {
    AnalyticsResponseBody(error: InstanceType<typeof Stripe.errors.StripeCardError>) {
        return {
            error_type: error.name,
            decline_code: error.decline_code ?? "unknown",
            funding_type: PaymentMethod.getFundingType(error.payment_method),
            payment_type: PaymentMethod.getPaymentType(error.payment_method),
        };
    }
};
